<template>
  <popup-dialog
    :visible="visible"
    class="popup-duplicatefolder"
    :close-button-visible="true"
    :title="$t('actions.moveFolder')"
    @close="close"
  >
    <form
      class="popup-duplicatefolder__form"
      @submit.prevent="tryMovingFolder()"
    >
      <input-dropdown
        :value="
          state.workspaceId == null ? null : workspaces[state.workspaceId]
        "
        :options="workspaces"
        prop="name"
        :label="$t('words.workspace', 1)"
        :searchable="true"
        group-key="organization"
        :error="state.fieldErrors?.workspace_id"
        @update:value="state.workspaceId = $event.id"
      />

      <button
        class="button popup-duplicatefolder__button mt-4"
        type="submit"
        :disabled="state.copying"
      >
        <template v-if="state.copying">
          {{ $t("messages.saving") }}
        </template>
        <template v-else>
          {{ $t("labels.move") }}
        </template>
      </button>
    </form>
  </popup-dialog>
</template>

<script setup lang="ts">
import { watch, reactive } from "vue";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputDropdown from "@core/input/InputDropdown.vue";

import { useWorkspaces } from "@/api/workspaces.store";
import { showError, showMessage } from "@core/ui/alert/alert";
import { getRouteToFolder } from "@core/routes/routeDetector";
import { BadRequestError } from "@core/errors/Errors";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import type { WorkspaceFolder } from "@/api/Workspace";

const props = withDefaults(
  defineProps<{
    visible?: boolean;
    folder?: { id: number; workspace: number; slug: string };
  }>(),
  {
    visible: false,
    folder: undefined
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "duplicated", event: WorkspaceFolder): void;
}>();

const { userWorkspace, workspaces, moveFolder } = useWorkspaces();
const { t } = useI18n();
const router = useRouter();

const state = reactive({
  workspaceId: undefined as number | undefined,
  folderSlug: undefined as string | undefined,
  fieldErrors: {} as Record<string, string>,

  copying: false
});

watch(
  () => props.folder?.id,
  () => {
    state.workspaceId = props.folder?.workspace ?? userWorkspace.value?.id;
  },
  { immediate: true }
);

async function tryMovingFolder() {
  if (state.workspaceId == null) {
    showMessage(t("messages.chooseWorkspace"));
    return;
  }

  if (props.folder == null) {
    return;
  }

  state.copying = true;
  try {
    const newFolder = await moveFolder(props.folder, state.workspaceId);

    router.push(getRouteToFolder(newFolder));
    close();
    emit("duplicated", newFolder);
  } catch (_error) {
    const error = _error as BadRequestError;
    state.fieldErrors = error.fields;
    showError(error);
  } finally {
    state.copying = false;
  }
}

function close() {
  emit("close");
}
</script>

<style lang="scss">
.popup-duplicatefolder {
  overflow: visible;

  &__form {
    padding: 2rem;
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
  }

  &__button {
    align-self: center;
  }
}
</style>
